import http from "./http";

export type DocumentType = "agreement" | "view_only";
export type DocumentTarget = "all_stations" | "exclusive_only";

export interface StationDocument {
	id: number;
	name: string;
	type: DocumentType;
	target: DocumentTarget;
	content?: string;
	updatedAt?: string;
	isActive: boolean;
}

export interface StationDocumentVersionRecord {
	id: number;
	version: number;
	versionLabel: string | null;
	content: string;
	isActive: boolean;
	requiresAcceptance: boolean;
	isPublished: boolean;
	publishedAt: string | null;
	publishedByUserId: number | null;
	publishedByUserName: string | null;
}

export interface SaveStationDocumentContentPayload {
	name: string;
	content: string;
	versionLabel: string;
	publishToStations: boolean;
}

// Definitions remain as local fallbacks for id/type/target when the backend has no stored config yet.
export const DOCUMENT_DEFINITIONS: Omit<StationDocument, "content" | "updatedAt" | "isActive">[] = [
	{ id: 1, name: "Business Cooperation Agreement",        type: "agreement", target: "all_stations"   },
	{ id: 2, name: "Exclusive Station Addendum",             type: "agreement", target: "all_stations"   },
	{ id: 3, name: "WhaleLO Station Cooperation Policy",    type: "agreement", target: "all_stations"   },
	{ id: 4, name: "Operation Guide (Full Version)",         type: "view_only", target: "all_stations"   },
	{ id: 5, name: "Quick Guide",                           type: "view_only", target: "all_stations"   },
	{ id: 6, name: "Payment Policy",                        type: "view_only", target: "all_stations"   },
	{ id: 7, name: "FAQ",                                   type: "view_only", target: "all_stations"   },
];

export async function getStationDocuments(): Promise<StationDocument[]> {
	const { data } = await http.get("/utility/station-documents");
	const remoteDocuments = Array.isArray(data?.data) ? data.data : [];

	return DOCUMENT_DEFINITIONS.map((definition) => {
		const matched = remoteDocuments.find((item: StationDocument) => Number(item?.id) === definition.id);
		return {
			...definition,
			name: typeof matched?.name === "string" && matched.name.trim() ? matched.name.trim() : definition.name,
			content: typeof matched?.content === "string" ? matched.content : "",
			updatedAt: typeof matched?.updatedAt === "string" ? matched.updatedAt : undefined,
			isActive: typeof matched?.isActive === "boolean" ? matched.isActive : false,
		};
	});
}

export async function getDocumentContent(id: number): Promise<string> {
	const { data } = await http.get(`/utility/station-documents/${id}/content`);
	return data?.content || "";
}

export async function getDocumentVersions(id: number): Promise<StationDocumentVersionRecord[]> {
	const { data } = await http.get(`/utility/station-documents/${id}/versions`);
	return Array.isArray(data?.data) ? data.data : [];
}

export async function saveDocumentContent(
	id: number,
	payload: SaveStationDocumentContentPayload
): Promise<void> {
	await http.post(`/utility/station-documents/${id}/content`, payload);
}

export async function toggleStationDocument(id: number, isActive: boolean): Promise<void> {
	await http.post("/utility/station-documents/toggle", { id, isActive });
}

/** Công tắc super admin "Bắt trạm ký văn bản" (06/10/2026). requireSigning null = chưa ai đặt (theo từng bản văn bản). */
export interface StationSigningSwitch {
	requireSigning: boolean | null;
	/** 28/10/2026: giấy phép được duyệt ⇒ trạm phải ký lại (có họ tên) mới rút được tiền. */
	resignAfterLicence?: boolean;
	updatedAt: string | null;
	updatedBy: string | null;
	canEdit: boolean;
}

export async function getStationSigningSwitch(): Promise<StationSigningSwitch> {
	const { data } = await http.get("/utility/station-signing-switch");
	return data?.data;
}

export async function setStationSigningSwitch(requireSigning: boolean): Promise<StationSigningSwitch> {
	const { data } = await http.put("/utility/station-signing-switch", { requireSigning });
	return data?.data;
}

export async function setStationResignSwitch(resignAfterLicence: boolean): Promise<StationSigningSwitch> {
	const { data } = await http.put("/utility/station-signing-switch", { resignAfterLicence });
	return data?.data;
}
